
import axios from 'axios';
import Swal from 'sweetalert2';
import Resumable from 'resumablejs';
import template from './MediaUploadComponent.html';
import getIconFromExtension from '../../filters/getIconFromExtension';
import DocumentMixin from '../../mixins/DocumentMixin';
import ImageMixin from '../../mixins/ImageMixin';

export default {
  template,

  mixins: [DocumentMixin, ImageMixin],

  props: {
    usedWithGalleryModule: {
      type: Boolean,
      default: false,
    },
    relatedModel: {
      type: String,
      required: true,
    },
    relatedId: {
      type: Number,
      required: true,
    },
    totalSimultaneousUploads: {
      type: Number,
      default: 4,
    },
    maxFilesToUpload: {
      type: Number,
      default: 4,
    },
    galleryFiles: {
      type: Array,
      default() {
        return [];
      },
    },
    role: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      resumable: new Resumable({
        target: '/admin/api/v1/upload/sendChunk',
        testChunks: false,
        prioritizeFirstAndLastChunk: false,
        forceChunkSize: true,
        chunkSize: 5 * 1024 * 1024,
        simultaneousUploads: this.totalSimultaneousUploads,
        maxFiles: this.maxFilesToUpload,
        throttleProgressCallbacks: 1,
        maxChunkRetries: 10,
        // Fix Axios interceptor overriding header vars:
        headers: () => ({
          Accept: 'application/json, text/plain, */*',
          'X-CSRF-TOKEN': window.Laravel.csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        }),
      }),

      // Set the global site_id
      site_id: window.site_id,

      // This is done because you are not supposed to mutate a prop directly
      files: this.galleryFiles,

      allAddedFilesReadyForUpload: false,
      uploading: false,
    };
  },

  filters: {
    getIconFromExtension,
  },

  watch: {
    files() {
      const filesReadyForUpload = this.files.filter(file => file.uploadId).filter(file => !file.completed);
      const filesExcludingAlreadyUploaded = this.files.filter(file => !file.completed);

      if (filesReadyForUpload.length === filesExcludingAlreadyUploaded.length) {
        this.allAddedFilesReadyForUpload = true;
      }
    },
    allAddedFilesReadyForUpload() {
      if (this.allAddedFilesReadyForUpload) {
        this.resumable.opts.query = {
          files: JSON.stringify(this.files),
        };

        this.resumable.upload();
      }
    },
  },

  mounted() {
    this.resumable.assignDrop(this.$el.querySelectorAll('.drop'));
    this.resumable.assignBrowse(this.$el.querySelectorAll('.drop'));

    if (!this.resumable.support) {
      Swal.fire('', 'Nettleseren din støtter ikke filopplasting.', 'error');
    }

    this.addEventListenerForFileAdded();
    this.addEventListenerForFileSuccess();
    this.addEventListenerForFileError();
    this.addEventListenerForComplete();
    this.addEventListenerForError();
  },

  methods: {
    addEventListenerForFileAdded() {
      this.resumable.on('fileAdded', (file) => {
        /**
         * If we have previously uploaded files,
         * we need to pause() or the upload will
         * start before our custom init.
         */
        if (this.allAddedFilesReadyForUpload) {
          this.resumable.pause();
        }

        this.allAddedFilesReadyForUpload = false;
        this.uploading = true;

        this.files.push({
          name: file.fileName,
          id: file.uniqueIdentifier,
          etags: [],
          completed: false,
        });

        this.startUpload(file.uniqueIdentifier);
      });
    },

    addEventListenerForFileSuccess() {
      this.resumable.on('fileSuccess', (file) => {
        /**
         * Loop through all of the file chunks.
         * This will contain the etags return
         * from our PHP script, we need these
         * to complete the file on S3.
         */
        const etags = file.chunks.map(chunk => JSON.parse(chunk.message()).etag);

        /**
         * Add the etags to the correct this.files
         * object.
         */
        this.files = this.files.map((fileItem) => {
          if (fileItem.id === file.uniqueIdentifier) {
            fileItem.etags = etags;
          }

          return fileItem;
        });

        this.finishRequest(file.uniqueIdentifier);
      });
    },

    addEventListenerForFileError() {
      this.resumable.on('fileError', (file, message) => {
        console.error(message);
        Swal.fire('Obs!', 'Noe gikk galt ved opplasting.', 'error');
      });
    },

    addEventListenerForComplete() {
      this.resumable.on('complete', () => {
        this.uploading = false;
      });
    },

    addEventListenerForError() {
      this.resumable.on('error', (message, file) => {
        console.error(message);
        Swal.fire('Obs!', 'Noe gikk galt.', 'error');
      });
    },

    startUpload(id) {
      this.uploading = true;

      axios.post('/admin/api/v1/upload/init', {
        fileName: this.resumable.getFromUniqueIdentifier(id).fileName,
      })
        .then((response) => {
          this.files = this.files.map((file) => {
            if (file.id === id) {
              file.uploadId = response.data.uploadId;
              file.fileName = response.data.fileName;
            }

            return file;
          });
        })
        .catch((error) => {
          this.uploading = false;
        });
    },

    finishRequest(id) {
      const file = this.getFile(id);

      axios.post('/admin/api/v1/upload/finishUpload', {
        uploadId: file.uploadId,
        fileName: file.fileName,
        name: file.name,
        etags: file.etags,
        siteId: this.site_id,
        usedWithGalleryModule: this.usedWithGalleryModule,
        relatedModel: this.relatedModel,
        relatedId: this.relatedId,
        type: this.type,
        role: this.role,

        // Parameters for image options
        image_options: this.imageOptions,
      }).then(
        (response) => {
          this.files = this.files.map((fileItem) => {
            if (fileItem.id === file.id) {
              fileItem.completed = true;
              fileItem.url = response.data.url;
              // Update id of file in order to allow delete requests
              fileItem.id = response.data.id;
              fileItem.created_at = response.data.created_at;

              if (response.data.file_type === 'image') {
                this.addImageNewFile(response.data);
                this.$emit('image:upload', response.data);
              } else {
                this.addDocumentNewFile(response.data);
                this.$emit('document:upload', response.data);
              }
            }

            return fileItem;
          });
        }, (error) => {
        },
      );
    },

    deleteFile(id) {
      const file = this.getFile(id);

      axios.delete(`/upload/${id}/destroy`).then(
        (response) => {
          const index = this.files.indexOf(file);

          Swal.fire('Fjernet!', 'Bilde ble fjernet fra galleriet', 'success');

          if (index !== -1) {
            this.files.splice(index, 1);
          }

          if (response.data.file_type === 'image') {
            this.deleteImageMedia(id);
            this.$emit('image:delete', id);
          } else {
            this.deleteDocumentMedia(id);
            this.$emit('document:delete', id);
          }
        },
        (error) => {
          console.error(error);
          Swal.fire('Obs!', 'Noe gikk galt ved sletting av filen.', 'error');
        },
      );
    },

    getFile(id) {
      return this.files.find(file => file.id === id);
    },

    cancelUpload() {
      this.resumable.cancel();
    },

    getFileProgressStyling(file) {
      // If file comes from database, return this
      if (file.created_at) {
        return 'linear-gradient(90deg, #FF9800 100%, #9E9E9E 100%)';
      }

      return `linear-gradient(90deg, #FF9800 ${
        this.resumable.getFromUniqueIdentifier(file.id).progress() * 100}%, #9E9E9E ${
        this.resumable.getFromUniqueIdentifier(file.id).progress() * 100}%)`;
    },
  },
};
